export const PRESET_OPTIONS = [
  { key: 'mom', label: "Mom / Mother's Day" },
  { key: 'partner', label: 'Partner / Anniversary' },
  { key: 'wedding', label: 'Wedding Favor' },
  { key: 'bestie', label: 'Best Friend' },
  { key: 'birthday', label: 'Birthday' },
  { key: 'graduation', label: 'Graduation' },
  { key: 'remembrance', label: 'Remembrance / Memorial' },
  { key: 'pet_memorial', label: 'Pet Memorial' },
  { key: 'new_baby', label: 'New Baby / Family' },
  { key: 'thank_you', label: 'Thank You' },
];

export const PAGE_EXPERIENCES = {
  gift_unboxing: {
    label: 'Gift Unboxing',
    shell: 'max-w-screen-md',
    header: '',
    note: 'Tap through the gift one beautiful layer at a time.',
  },
  layered_gift: {
    label: 'Guided Layers',
    shell: 'max-w-screen-md',
    header: '',
    note: 'Each module opens with its own small reveal, then guides the recipient to the next chapter.',
  },
  full_page: {
    label: 'Classic Story',
    shell: 'max-w-screen-md',
    header: '',
    note: 'A complete keepsake page with every memory in one place.',
  },
  classic: {
    label: 'Classic Keepsake',
    shell: 'max-w-screen-md',
    header: '',
    note: 'Read the whole gift as one polished keepsake page.',
  },
  envelope: {
    label: 'Envelope',
    shell: 'max-w-3xl bg-white/45 border-x-4 border-white/70 shadow-[0_30px_90px_rgba(90,74,107,0.10)]',
    header: 'rounded-[2rem] rotate-[-0.5deg]',
    note: 'A letter-style gift, unfolded slowly and personally.',
  },
  photo_album: {
    label: 'Photo Album',
    shell: 'max-w-screen-md bg-gradient-to-b from-white/20 via-transparent to-white/20',
    header: 'rounded-[2.25rem]',
    note: 'A photo-first gift with small captions and album-like pacing.',
  },
  memory_journey: {
    label: 'Photo Journey',
    shell: 'max-w-screen-md bg-gradient-to-b from-white/20 via-transparent to-white/20',
    header: 'rounded-[2.25rem]',
    note: 'Follow the page like a path through shared moments.',
  },
  surprise_lock: {
    label: 'Surprise Reveal',
    shell: 'max-w-screen-md bg-white/20',
    header: 'rounded-[2.5rem]',
    note: 'Each section feels like another unlocked surprise.',
  },
  quiet: {
    label: 'Quiet Keepsake',
    shell: 'max-w-2xl bg-white/20',
    header: 'rounded-[2rem] shadow-none',
    note: 'A softer page rhythm for gentle memories.',
  },
  poster: {
    label: 'Keepsake Poster',
    shell: 'max-w-2xl bg-white/20',
    header: 'rounded-[2rem] shadow-none',
    note: 'End around one beautiful card they can save.',
  },
};

export const GIFT_FORMATS = {
  layer_by_layer: {
    label: 'Guided Layers / Layer-by-layer Gift',
    description: 'Our recommended style: every module gets its own reveal, then guides the recipient to the next layer.',
    openingMode: 'layered_gift',
    recommended: true,
    bestFor: 'All NFC gift themes, especially gifts with multiple modules that should not appear as one long page',
    recommendedModules: ['primary_media', 'text', 'surprise_box', 'voice_postcard', 'guestbook', 'keepsake_poster'],
  },
  memory_page: {
    label: 'Classic Keepsake',
    description: 'A complete story with photos, a letter, voice, blessings, and a final card.',
    openingMode: 'classic',
    bestFor: 'Family memories, thank-you gifts, simple NFC keepsakes',
    recommendedModules: ['primary_media', 'text', 'voice_postcard', 'guestbook', 'keepsake_poster'],
  },
  gift_reveal: {
    label: 'Gift Unboxing',
    description: 'A step-by-step reveal that feels closer to opening a real wrapped gift.',
    openingMode: 'gift_unboxing',
    bestFor: 'Birthdays, anniversaries, romantic surprises',
    recommendedModules: ['primary_media', 'surprise_box', 'voice_postcard', 'guestbook', 'keepsake_poster'],
  },
  photo_journey: {
    label: 'Photo Album',
    description: 'A photo-first path through shared memories, milestones, and little moments.',
    openingMode: 'photo_album',
    bestFor: 'Trips, babies, graduation, friendship, family stories',
    recommendedModules: ['primary_media', 'text', 'voice_postcard', 'keepsake_poster'],
  },
  letters_for_later: {
    label: 'Open When',
    description: 'A letter-led gift with messages they can open slowly and return to later.',
    openingMode: 'envelope',
    bestFor: 'Partners, mom, encouragement, long-distance, memorial gifts',
    recommendedModules: ['text', 'surprise_box', 'voice_postcard', 'keepsake_poster'],
  },
  keepsake_moment: {
    label: 'Keepsake Poster',
    description: 'A poster-style gift built around one elegant, saveable final moment.',
    openingMode: 'poster',
    bestFor: 'Framed gifts, cards, candles, memorial, elegant appreciation gifts',
    recommendedModules: ['primary_media', 'text', 'guestbook', 'keepsake_poster'],
  },
  surprise_reveal: {
    label: 'Surprise Lock',
    description: 'A playful unlock flow where each tap, hold, or reveal opens the next moment.',
    openingMode: 'surprise_lock',
    bestFor: 'Playful birthdays, best friends, proposals, and hidden-message gifts',
    recommendedModules: ['primary_media', 'surprise_box', 'voice_postcard', 'keepsake_poster'],
  },
};

export const GIFT_FORMAT_OPTIONS = Object.entries(GIFT_FORMATS)
  .map(([key, value]) => ({
    key,
    ...value,
  }))
  .sort((a, b) => {
    if (a.key === 'layer_by_layer') return -1;
    if (b.key === 'layer_by_layer') return 1;
    return 0;
  });

export const getGiftFormatForOpeningMode = (openingMode = 'layered_gift') => {
  const aliases = {
    full_page: 'classic',
    memory_journey: 'photo_album',
    quiet: 'poster',
  };
  const normalizedOpeningMode = aliases[openingMode] || openingMode;
  const match = GIFT_FORMAT_OPTIONS.find((format) => format.openingMode === normalizedOpeningMode);
  return match?.key || 'layer_by_layer';
};
